import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  robots?: string;
  canonical?: string;
  imageWidth?: string;
  imageHeight?: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
}

export function SEO({
  title,
  description,
  keywords,
  image = 'https://www.metafitwellness.com/metafit.webp',
  url = 'https://www.metafitwellness.com/',
  type = 'website',
  siteName = 'MetaFit Wellness',
  robots = 'index, follow',
  canonical,
  imageWidth = '1200',
  imageHeight = '630',
  schema,
}: SEOProps) {
  const defaultKeywords =
    'MetaFit, MetaFit Wellness, wellness, fitness, health, yoga, therapy, healers, ayurveda, mental wellness, physiotherapy, lab test, nutrition, wellness retreats';
  const finalKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  const finalCanonical = canonical || url;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robots} />
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      {imageWidth && <meta property="og:image:width" content={imageWidth} />}
      {imageHeight && <meta property="og:image:height" content={imageHeight} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {url && <meta name="twitter:url" content={url} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {/* JSON-LD Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
